import { MarkdownText } from './Markdown'

interface Props {
  children: string
  /** Override card background tint; default warm white. */
  cardClassName?: string
}

interface ParsedSection {
  title: string | null
  body: string
}

/**
 * Renders a long AI-produced reading as a vertical stack of CARDS — each
 * H3-marked section becomes its own readable block, separated by gaps.
 *
 * Why this exists: putting the whole 3000-word natal interpretation (or a
 * 4-section weekly forecast) into one `<MarkdownText>` produces a wall of
 * text that's painful to scan on mobile. Splitting into cards gives natural
 * stopping points and a clearer hierarchy.
 *
 * Two detection paths:
 *   1. **Markdown headings** (`### Title\nbody`) — the format the AI is
 *      asked to output. Clean and unambiguous.
 *   2. **Heuristic** — for older cached texts without `###` markers, we
 *      detect "looks like a heading" lines (short, no terminal punctuation,
 *      surrounded by blank lines). Less precise but salvages legacy content.
 *
 * Either way every section gets its own card, with the title as a serif h3
 * at the top, body as plain markdown.
 */
export function SectionedMarkdown({ children, cardClassName }: Props) {
  const sections = parseSections(children)
  return (
    <div className="flex flex-col gap-3">
      {sections.map((sec, i) => (
        <div
          key={i}
          className={
            cardClassName ??
            'rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60'
          }
        >
          {sec.title && (
            <h3 className="mb-3 font-serif text-xl leading-tight text-sage-800">
              {sec.title}
            </h3>
          )}
          <MarkdownText>{sec.body}</MarkdownText>
        </div>
      ))}
    </div>
  )
}

function parseSections(text: string): ParsedSection[] {
  const trimmed = text.trim()
  if (!trimmed) return []

  // Path 1: explicit H3 markers
  const markerRe = /^(?:#{2,3})\s+([^\n]+)\n?([\s\S]*?)(?=^(?:#{2,3})\s|\Z)/gms
  const markerSections: ParsedSection[] = []
  let lastEnd = 0
  let m: RegExpExecArray | null
  while ((m = markerRe.exec(trimmed)) !== null) {
    if (m.index > lastEnd) {
      const intro = trimmed.slice(lastEnd, m.index).trim()
      if (intro) markerSections.push({ title: null, body: intro })
    }
    markerSections.push({ title: cleanTitle(m[1]!), body: m[2]!.trim() })
    lastEnd = m.index + m[0].length
  }
  if (markerSections.length > 0) {
    if (lastEnd < trimmed.length) {
      const tail = trimmed.slice(lastEnd).trim()
      if (tail) markerSections.push({ title: null, body: tail })
    }
    return markerSections
  }

  // Path 2: heuristic — detect short heading-like paragraphs
  const paragraphs = trimmed.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
  const out: ParsedSection[] = []
  let currentTitle: string | null = null
  let currentBody: string[] = []

  const flush = () => {
    // Sections with title but no body are usually AI meta-headers like
    // "Недельный прогноз для Dev Ядвига, 4–10 мая 2026" — they sit alone as
    // a paragraph and our heuristic flagged them as titles, but the next
    // real heading immediately follows so the body stays empty. Drop them
    // to avoid an empty card with just a title.
    if (currentTitle && currentBody.length === 0) {
      currentTitle = null
      return
    }
    if (currentTitle || currentBody.length > 0) {
      out.push({ title: currentTitle, body: currentBody.join('\n\n') })
    }
    currentTitle = null
    currentBody = []
  }

  for (const p of paragraphs) {
    if (isLikelyHeading(p)) {
      flush()
      currentTitle = cleanTitle(p)
    } else {
      currentBody.push(p)
    }
  }
  flush()

  return out.length > 0 ? out : [{ title: null, body: trimmed }]
}

function cleanTitle(raw: string): string {
  return raw
    .replace(/^#+\s*/, '') // leading hashes if anything slipped through
    .replace(/^\*+\s*|\s*\*+$/g, '') // wrapping bold markers **Title**
    .replace(/^_+\s*|\s*_+$/g, '') // wrapping italic _Title_
    .replace(/^\d+\.\s+/, '') // leading numeric prefixes "1. Title"
    .trim()
}

function isLikelyHeading(line: string): boolean {
  // Single physical line only
  if (line.includes('\n')) return false
  const stripped = line.replace(/^\*+|\*+$/g, '').replace(/^#+\s*/, '').trim()
  if (stripped.length < 4) return false
  if (stripped.length > 70) return false
  // Prose almost always ends in terminal punctuation; heading rarely does
  if (/[.!?…]$/.test(stripped)) return false
  return true
}
